import { useState, useEffect, useCallback } from "react";
import { useNavigate } from "react-router-dom";
import { supabase, diagnoseConnection } from "@/lib/supabase";
import { withTimeout } from "@/lib/withTimeout";
import Navbar from "@/components/feature/Navbar";
import Footer from "@/components/feature/Footer";
import { useAuth } from "@/hooks/useAuth";
import { useWallet, clearWalletCache } from "@/hooks/useWallet";
import WithdrawalPasswordModal from "./components/WithdrawalPasswordModal";
import IDCardUpload from "./components/IDCardUpload";

/* ── Bank account masking helper ─────────────── */
function maskBankAccount(account: string): string {
  if (!account || account.length <= 6) return account;
  return account.slice(0, account.length - 6) + "******";
}

/* ── Withdrawal status display map ───────────── */
const WITHDRAWAL_STATUS_MAP: Record<
  string,
  { label: string; bg: string; text: string; border: string }
> = {
  pending: {
    label: "審核中",
    bg: "bg-amber-50",
    text: "text-amber-700",
    border: "border-amber-200",
  },
  "已完成": {
    label: "已完成",
    bg: "bg-emerald-50",
    text: "text-emerald-700",
    border: "border-emerald-200",
  },
  approved: {
    label: "已核准",
    bg: "bg-emerald-50",
    text: "text-emerald-700",
    border: "border-emerald-200",
  },
  completed: {
    label: "已完成",
    bg: "bg-emerald-50",
    text: "text-emerald-700",
    border: "border-emerald-200",
  },
  rejected: {
    label: "已拒絕",
    bg: "bg-red-50",
    text: "text-red-600",
    border: "border-red-200",
  },
  "被駁回": {
    label: "已拒絕",
    bg: "bg-red-50",
    text: "text-red-600",
    border: "border-red-200",
  },
  "已駁回": {
    label: "已拒絕",
    bg: "bg-red-50",
    text: "text-red-600",
    border: "border-red-200",
  },
  denied: {
    label: "已拒絕",
    bg: "bg-red-50",
    text: "text-red-600",
    border: "border-red-200",
  },
  "已拒絕": {
    label: "已拒絕",
    bg: "bg-red-50",
    text: "text-red-600",
    border: "border-red-200",
  },
  cancelled: {
    label: "已取消",
    bg: "bg-stone-50",
    text: "text-stone-500",
    border: "border-stone-200",
  },
};

export default function WithdrawalPage() {
  const { user, profile, isLoggedIn, loading: authLoading, signOut, fetchProfile } = useAuth();
  const { wallet } = useWallet();
  const navigate = useNavigate();

  // --- Connection diagnostic ---
  const [diagStatus, setDiagStatus] = useState<string | null>(null);

  // --- Withdrawal form state ---
  const [amount, setAmount] = useState("");
  const [submitting, setSubmitting] = useState(false);
  const [showPasswordModal, setShowPasswordModal] = useState(false);
  const [successMsg, setSuccessMsg] = useState("");
  const [errorMsg, setErrorMsg] = useState("");

  // --- Bank / verification state ---
  const [fullName, setFullName] = useState("");
  const [bankNameInput, setBankNameInput] = useState("");
  const [branchCodeInput, setBranchCodeInput] = useState("");
  const [bankAccountInput, setBankAccountInput] = useState("");
  const [accountHolderInput, setAccountHolderInput] = useState("");
  const [savingBankInfo, setSavingBankInfo] = useState(false);
  const [bankSaveError, setBankSaveError] = useState("");
  const [bankSaveSuccess, setBankSaveSuccess] = useState("");

  // --- ID card images ---
  const [idFront, setIdFront] = useState<string | null>(null);
  const [idBack, setIdBack] = useState<string | null>(null);

  // Redirect if not logged in
  useEffect(() => {
    if (!authLoading && !isLoggedIn) {
      navigate("/?showLogin=1", { replace: true });
    }
  }, [authLoading, isLoggedIn, navigate]);

  // Connection diagnosis on page load
  useEffect(() => {
    const runDiag = async () => {
      const result = await diagnoseConnection();
      if (!result.connected) {
        console.error("[WithdrawalPage] 連線診斷失敗:", result.error);
        setDiagStatus(result.error || "無法連線");
      } else {
        console.log("[WithdrawalPage] 連線診斷成功，sessionOK:", result.sessionOk);
        setDiagStatus(null);
      }
    };
    runDiag();
  }, []);

  // 封鎖狀態的帳號禁止進入提款頁面
  useEffect(() => {
    if (profile?.account_status === "banned") {
      window.alert("帳號已被封鎖，請聯繫客服");
      signOut();
      navigate("/?showLogin=1", { replace: true });
    }
  }, [profile, signOut, navigate]);

  // Sync bank inputs + ID card images from profile whenever profile updates
  useEffect(() => {
    if (profile) {
      setFullName(profile.full_name || "");
      setBankNameInput(profile.bank_name || "");
      setBranchCodeInput(profile.branch_code || "");
      setBankAccountInput(profile.bank_account || "");
      setAccountHolderInput(profile.account_holder || "");
      setIdFront(profile.id_card_front || null);
      setIdBack(profile.id_card_back || null);
    }
  }, [profile]);

  const balance = wallet?.balance ?? 0;
  const verificationStatus = profile?.verification_status ?? "unverified";
  const isVerified = verificationStatus === "approved";
  const isBanned = profile?.account_status === "banned";
  const isSuspended = profile?.account_status === "suspended" || profile?.account_status === "paused";
  const isFormReadOnly = verificationStatus === "pending" || verificationStatus === "approved";
  const hasBankInfo = bankNameInput && bankAccountInput;

  const getVerificationBadge = () => {
    switch (verificationStatus) {
      case "approved":
        return {
          label: "已認證",
          icon: "ri-checkbox-circle-line",
          class: "bg-emerald-50 text-emerald-700 border-emerald-200",
        };
      case "pending":
        return {
          label: "審核中",
          icon: "ri-time-line",
          class: "bg-amber-50 text-amber-700 border-amber-200",
        };
      case "rejected":
        return {
          label: "已拒絕",
          icon: "ri-close-circle-line",
          class: "bg-red-50 text-red-600 border-red-200",
        };
      default:
        return {
          label: "未認證",
          icon: "ri-error-warning-line",
          class: "bg-stone-100 text-stone-500 border-stone-200",
        };
    }
  };
  const vBadge = getVerificationBadge();

  const handleAmountChange = (e: React.ChangeEvent<HTMLInputElement>) => {
    const val = e.target.value.replace(/[^0-9]/g, "");
    setAmount(val);
    setErrorMsg("");
    setSuccessMsg("");
  };

  const handleBankInputChange = (
    setter: React.Dispatch<React.SetStateAction<string>>
  ) => (e: React.ChangeEvent<HTMLInputElement>) => {
    setter(e.target.value);
    setBankSaveError("");
    setBankSaveSuccess("");
  };

  // Save verification & bank info
  const handleSaveBankInfo = useCallback(async () => {
    if (!user) return;

    if (!fullName.trim()) {
      setBankSaveError("請輸入真實姓名");
      return;
    }
    if (!bankNameInput.trim()) {
      setBankSaveError("請輸入銀行名稱");
      return;
    }
    if (!bankAccountInput.trim()) {
      setBankSaveError("請輸入銀行帳號");
      return;
    }
    if (!accountHolderInput.trim()) {
      setBankSaveError("請輸入戶名");
      return;
    }
    if (!idFront || !idBack) {
      setBankSaveError("請上傳身分證正面與反面照片");
      return;
    }

    setSavingBankInfo(true);
    setBankSaveError("");
    setBankSaveSuccess("");

    try {
      const { data: updateData, error } = await withTimeout(
        supabase
          .from("profiles")
          .update({
            full_name: fullName.trim(),
            bank_name: bankNameInput.trim(),
            branch_code: branchCodeInput.trim() || null,
            bank_account: bankAccountInput.trim(),
            account_holder: accountHolderInput.trim(),
            verification_status: "pending",
            is_verified: false,
            id_card_front: idFront,
            id_card_back: idBack,
          })
          .eq("id", user.id)
          .select("verification_status"),
        45000,
        "儲存實名認證資料",
      );

      if (error) {
        console.error("[saveBankInfo] update error:", error);
        setBankSaveError(`儲存失敗：${error.message}`);
      } else if (!updateData || updateData.length === 0) {
        console.error("[saveBankInfo] update returned no rows — possible RLS block or missing profile");
        setBankSaveError("儲存失敗：無法更新資料（請確認登入狀態或聯繫客服）");
      } else {
        setBankSaveSuccess("實名認證資料已提交，請等待客服審核通過後即可提款");
        // 背景刷新會員資料，不阻塞「儲存中」按鈕，避免照片較大時畫面一直轉圈
        if (fetchProfile && user.id) {
          fetchProfile(user.id).catch((e) => {
            console.warn("[saveBankInfo] background profile refresh failed:", e);
          });
        }
      }
    } catch (err: unknown) {
      console.error("[saveBankInfo] exception:", err);
      const raw = err instanceof Error ? err.message : "";
      if (raw.includes("超時")) {
        setBankSaveError(
          "網路連線較慢，儲存還沒完成。請確認手機訊號穩定後，再按一次「提交審核並儲存」，重複送出不會造成資料重複。",
        );
      } else {
        setBankSaveError(raw || "儲存過程發生錯誤，請稍後再試");
      }
    } finally {
      setSavingBankInfo(false);
    }
  }, [
    user,
    fullName,
    bankNameInput,
    branchCodeInput,
    bankAccountInput,
    accountHolderInput,
    idFront,
    idBack,
    fetchProfile,
  ]);

  const handleSubmitClick = () => {
    if (isBanned) {
      setErrorMsg("帳號已被封鎖，無法進行提款操作，請聯繫客服人員。");
      return;
    }
    if (isSuspended) {
      setErrorMsg("帳號已被暫停，暫時無法進行提款操作，請聯繫客服。");
      return;
    }
    if (!isVerified) {
      if (verificationStatus === "pending") {
        setErrorMsg("實名認證審核中，請等待管理員審核通過後再進行提款");
      } else if (verificationStatus === "rejected") {
        setErrorMsg("實名認證已被拒絕，請聯繫客服或重新提交認證資料");
      } else {
        setErrorMsg("請先完成實名認證與銀行綁定");
      }
      return;
    }
    const num = parseInt(amount, 10);
    if (!amount || num <= 0) {
      setErrorMsg("請輸入有效的提款金額");
      return;
    }
    if (num > balance) {
      setErrorMsg("提款金額不可超過可用餘額");
      return;
    }
    if (!hasBankInfo) {
      setErrorMsg("尚未設定銀行帳戶資訊，請先完成實名認證");
      return;
    }
    setShowPasswordModal(true);
  };

  const handlePasswordVerified = async () => {
    setShowPasswordModal(false);

    const num = parseInt(amount, 10);
    if (!user || !num || num <= 0) return;

    setSubmitting(true);
    setErrorMsg("");
    setSuccessMsg("");

    try {
      // 1. 原子讀取 wallets 最新餘額（防止併發導致超提）
      const { data: walletRow, error: walletReadErr } = await supabase
        .from("wallets")
        .select("balance")
        .eq("user_id", user.id)
        .maybeSingle();

      if (walletReadErr) {
        console.error("[withdraw] wallet read error:", walletReadErr);
        setErrorMsg(`餘額檢查失敗：${walletReadErr.message}`);
        setSubmitting(false);
        return;
      }

      const currentBalance = walletRow?.balance ?? 0;
      if (currentBalance < num) {
        window.alert("餘額不足，無法提現！");
        setSubmitting(false);
        return;
      }

      const newBalance = currentBalance - num;

      // 2. 真資料庫扣減 wallets.balance
      const { error: deductErr } = await supabase
        .from("wallets")
        .update({ balance: newBalance })
        .eq("user_id", user.id);

      if (deductErr) {
        console.error("[withdraw] wallet deduct error:", deductErr);
        setErrorMsg(`餘額扣減失敗：${deductErr.message}`);
        setSubmitting(false);
        return;
      }

      // 3. 同步寫入流水帳 + 提領審核紀錄（並行，互不阻塞）
      const [txRes, wdRes, profRes] = await Promise.all([
        supabase.from("wallet_transactions").insert({
          user_id: user.id,
          amount: -num,
          type: "withdraw",
          description: "會員申請提現",
        }),
        supabase.from("withdrawals").insert({
          user_id: user.id,
          amount: num,
          status: "pending",
          type: "withdrawal",
          bank_name: bankNameInput,
          bank_account: bankAccountInput,
          branch_code: branchCodeInput || null,
          account_holder: accountHolderInput || null,
          user_name: profile?.name || profile?.full_name || null,
        }),
        // 兼容性：同步更新 profiles.balance（舊邏輯兜底）
        supabase
          .from("profiles")
          .update({ balance: newBalance })
          .eq("id", user.id),
      ]);

      if (txRes.error) {
        console.error("[withdraw] wallet_transactions insert error:", txRes.error);
      }
      if (profRes.error) {
        console.error("[withdraw] profiles balance update error:", profRes.error);
      }
      if (wdRes.error) {
        console.error("[withdraw] withdrawals insert error:", wdRes.error);
        setErrorMsg(`提款紀錄建立失敗：${wdRes.error.message}`);
        setSubmitting(false);
        return;
      }

      // 4. 清除快取並即時刷新前台餘額
      clearWalletCache(user.id);
      window.dispatchEvent(new Event("wallet:refresh"));

      setSuccessMsg("提現申請已送出，等待審核中！");
      setAmount("");
    } catch (err: unknown) {
      console.error("[withdraw] exception:", err);
      const msg = err instanceof Error ? err.message : "提款過程發生錯誤";
      setErrorMsg(msg);
    } finally {
      setSubmitting(false);
    }
  };

  if (authLoading) {
    return (
      <div className="min-h-screen bg-white">
        <Navbar />
        <div className="pt-32 text-center">
          <i className="ri-loader-4-line text-3xl text-stone-300 animate-spin" />
          <p className="font-body text-sm text-stone-400 mt-3">載入中...</p>
        </div>
      </div>
    );
  }

  if (!isLoggedIn) {
    return (
      <div className="min-h-screen bg-white">
        <Navbar />
        <div className="pt-32 text-center">
          <p className="font-body text-sm text-stone-400">重新導向至首頁...</p>
        </div>
      </div>
    );
  }

  return (
    <div className="min-h-screen bg-white">
      <Navbar />

      <WithdrawalPasswordModal
        isOpen={showPasswordModal}
        onClose={() => setShowPasswordModal(false)}
        onVerified={handlePasswordVerified}
        userId={user?.id || ""}
        onAccountSuspended={() => {
          setShowPasswordModal(false);
          setErrorMsg("帳號已被暫停，暫時無法申請提現，請聯繫客服。");
          if (fetchProfile && user?.id) {
            fetchProfile(user.id);
          }
        }}
      />

      <main className="pt-20 md:pt-24">
        {/* Header */}
        <section className="bg-stone-900 py-10 md:py-14">
          <div className="w-full px-4 md:px-8 lg:px-12">
            <div className="max-w-3xl mx-auto text-center">
              <h1 className="font-display text-xl md:text-2xl font-bold text-white">
                <i className="ri-money-cny-circle-line mr-2" />
                提款中心
              </h1>
              <p className="font-body text-xs text-stone-400 mt-2">
                安全、快速，審核通過後即刻入帳
              </p>
            </div>
          </div>
        </section>

        <div className="w-full px-4 md:px-8 lg:px-12 py-8 md:py-12">
          <div className="max-w-3xl mx-auto">
            {/* Connection diagnostic warning */}
            {diagStatus && (
              <div className="bg-red-50 border border-red-200 rounded-xl p-3 flex items-start gap-2 mb-4">
                <div className="w-4 h-4 flex items-center justify-center flex-shrink-0 mt-0.5">
                  <i className="ri-error-warning-line text-red-500 text-sm" />
                </div>
                <p className="font-body text-xs text-red-600">
                  <strong>連線異常：</strong>{diagStatus}，請檢查網路連線後重新整理頁面。
                </p>
              </div>
            )}

            {/* Balance Card */}
            <div className="bg-white rounded-2xl border border-stone-200 overflow-hidden">
              <div className="px-6 py-5 border-b border-stone-100">
                <h2 className="font-display text-base font-bold text-stone-800">
                  <i className="ri-wallet-3-line text-rose-700 mr-1.5" />
                  可用餘額
                </h2>
              </div>
              <div className="p-6 text-center">
                <p className="font-display text-4xl md:text-5xl font-bold text-rose-800">
                  NT$ {balance.toLocaleString()}
                </p>
                <p className="font-body text-xs text-stone-400 mt-2">
                  提款金額將於審核通過後匯入您的銀行帳戶
                </p>
              </div>
            </div>

            {/* ===== Real-Name Verification & Bank Info ===== */}
            <div className="bg-white rounded-2xl border border-stone-200 overflow-hidden mt-6">
              <div className="px-6 py-5 border-b border-stone-100">
                <h2 className="font-display text-base font-bold text-stone-800">
                  <i className="ri-shield-check-line text-rose-700 mr-1.5" />
                  實名認證與銀行綁定
                </h2>
              </div>
              <div className="p-6 flex flex-col gap-4">
                {/* Verification status badge */}
                <div className="flex items-center gap-2 mb-2">
                  <span className={`inline-flex items-center gap-1 font-body text-[11px] font-semibold px-2.5 py-1 rounded-full border ${vBadge.class}`}>
                    <i className={vBadge.icon} />
                    {vBadge.label}
                  </span>
                  {verificationStatus === "pending" && (
                    <span className="font-body text-[11px] text-amber-600">
                      管理員審核中，請耐心等待
                    </span>
                  )}
                  {verificationStatus === "rejected" && (
                    <span className="font-body text-[11px] text-red-600">
                      認證被拒絕，請聯繫客服或重新提交資料
                    </span>
                  )}
                </div>

                {/* Full Name */}
                <div>
                  <label className="font-body text-xs font-medium text-stone-500 mb-1.5 block">
                    真實姓名（實名認證）
                  </label>
                  <input
                    type="text"
                    value={fullName}
                    onChange={handleBankInputChange(setFullName)}
                    placeholder="請輸入您的真實姓名"
                    disabled={isFormReadOnly}
                    className="w-full font-body text-sm px-4 py-2.5 rounded-xl border border-stone-200 focus:border-rose-400 focus:ring-2 focus:ring-rose-100 outline-none transition-all placeholder:text-stone-300 disabled:bg-stone-50 disabled:text-stone-400 disabled:cursor-not-allowed"
                  />
                </div>

                <div className="grid grid-cols-1 sm:grid-cols-2 gap-4">
                  {/* Bank Name */}
                  <div>
                    <label className="font-body text-xs font-medium text-stone-500 mb-1.5 block">
                      銀行名稱
                    </label>
                    <input
                      type="text"
                      value={bankNameInput}
                      onChange={handleBankInputChange(setBankNameInput)}
                      placeholder="例如：台灣銀行"
                      disabled={isFormReadOnly}
                      className="w-full font-body text-sm px-4 py-2.5 rounded-xl border border-stone-200 focus:border-rose-400 focus:ring-2 focus:ring-rose-100 outline-none transition-all placeholder:text-stone-300 disabled:bg-stone-50 disabled:text-stone-400 disabled:cursor-not-allowed"
                    />
                  </div>

                  {/* Branch Code */}
                  <div>
                    <label className="font-body text-xs font-medium text-stone-500 mb-1.5 block">
                      分行代碼
                    </label>
                    <input
                      type="text"
                      value={branchCodeInput}
                      onChange={handleBankInputChange(setBranchCodeInput)}
                      placeholder="例如：0040041"
                      disabled={isFormReadOnly}
                      className="w-full font-body text-sm px-4 py-2.5 rounded-xl border border-stone-200 focus:border-rose-400 focus:ring-2 focus:ring-rose-100 outline-none transition-all placeholder:text-stone-300 disabled:bg-stone-50 disabled:text-stone-400 disabled:cursor-not-allowed"
                    />
                  </div>

                  {/* Bank Account */}
                  <div>
                    <label className="font-body text-xs font-medium text-stone-500 mb-1.5 block">
                      銀行帳號
                    </label>
                    <input
                      type="text"
                      inputMode="numeric"
                      value={isFormReadOnly ? maskBankAccount(bankAccountInput) : bankAccountInput}
                      onChange={handleBankInputChange(setBankAccountInput)}
                      onPaste={(e) => {
                        e.preventDefault();
                        setBankSaveError("銀行帳號請手動輸入，不可複製貼上");
                        setTimeout(() => setBankSaveError(""), 3000);
                      }}
                      onCopy={(e) => {
                        e.preventDefault();
                      }}
                      onCut={(e) => {
                        e.preventDefault();
                      }}
                      placeholder="請輸入銀行帳號"
                      disabled={isFormReadOnly}
                      className="w-full font-body text-sm px-4 py-2.5 rounded-xl border border-stone-200 focus:border-rose-400 focus:ring-2 focus:ring-rose-100 outline-none transition-all placeholder:text-stone-300 disabled:bg-stone-50 disabled:text-stone-400 disabled:cursor-not-allowed"
                    />
                    {!isFormReadOnly && (
                      <p className="font-body text-[11px] text-stone-400 mt-1">
                        為保障帳戶安全，銀行帳號僅能手動輸入
                      </p>
                    )}
                  </div>

                  {/* Account Holder */}
                  <div>
                    <label className="font-body text-xs font-medium text-stone-500 mb-1.5 block">
                      戶名
                    </label>
                    <input
                      type="text"
                      value={accountHolderInput}
                      onChange={handleBankInputChange(setAccountHolderInput)}
                      placeholder="請輸入帳戶戶名"
                      disabled={isFormReadOnly}
                      className="w-full font-body text-sm px-4 py-2.5 rounded-xl border border-stone-200 focus:border-rose-400 focus:ring-2 focus:ring-rose-100 outline-none transition-all placeholder:text-stone-300 disabled:bg-stone-50 disabled:text-stone-400 disabled:cursor-not-allowed"
                    />
                  </div>
                </div>

                {/* ID Card Upload */}
                <div className="mt-2">
                  <IDCardUpload
                    frontImage={idFront}
                    backImage={idBack}
                    onFrontChange={setIdFront}
                    onBackChange={setIdBack}
                    disabled={verificationStatus === "pending" || verificationStatus === "approved"}
                  />
                </div>

                {/* Status hint box */}
                {verificationStatus === "pending" ? (
                  <div className="bg-amber-50 border border-amber-200 rounded-xl p-3 flex items-start gap-2">
                    <div className="w-4 h-4 flex items-center justify-center flex-shrink-0 mt-0.5">
                      <i className="ri-time-line text-amber-600 text-sm" />
                    </div>
                    <p className="font-body text-xs text-amber-700 leading-relaxed">
                      <strong className="font-semibold">審核中：</strong>
                      您的實名認證資料已提交，目前正在審核中，請耐心等待管理員審核結果。
                    </p>
                  </div>
                ) : verificationStatus === "approved" ? (
                  <div className="bg-emerald-50 border border-emerald-200 rounded-xl p-3 flex items-start gap-2">
                    <div className="w-4 h-4 flex items-center justify-center flex-shrink-0 mt-0.5">
                      <i className="ri-checkbox-circle-line text-emerald-600 text-sm" />
                    </div>
                    <p className="font-body text-xs text-emerald-700 leading-relaxed">
                      <strong className="font-semibold">已通過：</strong>
                      實名認證已審核通過，您可正常進行提款申請。
                    </p>
                  </div>
                ) : verificationStatus === "rejected" ? (
                  <div className="bg-red-50 border border-red-200 rounded-xl p-3 flex items-start gap-2">
                    <div className="w-4 h-4 flex items-center justify-center flex-shrink-0 mt-0.5">
                      <i className="ri-close-circle-line text-red-500 text-sm" />
                    </div>
                    <p className="font-body text-xs text-red-600 leading-relaxed">
                      <strong className="font-semibold">已拒絕：</strong>
                      認證資料審核未通過，請聯繫客服或重新提交正確資料。
                    </p>
                  </div>
                ) : (
                  <div className="bg-amber-50 border border-amber-200 rounded-xl p-3 flex items-start gap-2">
                    <div className="w-4 h-4 flex items-center justify-center flex-shrink-0 mt-0.5">
                      <i className="ri-error-warning-line text-amber-600 text-sm" />
                    </div>
                    <p className="font-body text-xs text-amber-700 leading-relaxed">
                      <strong className="font-semibold">注意：</strong>
                      資訊一旦提交，如需修改請聯繫客服。請確保所有資料正確無誤，以免影響提款入帳。
                    </p>
                  </div>
                )}

                {/* Save button */}
                {bankSaveError && (
                  <div className="bg-red-50 border border-red-200 rounded-xl p-3 flex items-start gap-2">
                    <div className="w-4 h-4 flex items-center justify-center flex-shrink-0 mt-0.5">
                      <i className="ri-error-warning-line text-red-500 text-sm" />
                    </div>
                    <p className="font-body text-xs text-red-600">{bankSaveError}</p>
                  </div>
                )}
                {bankSaveSuccess && (
                  <div className="bg-emerald-50 border border-emerald-200 rounded-xl p-3 flex items-start gap-2">
                    <div className="w-4 h-4 flex items-center justify-center flex-shrink-0 mt-0.5">
                      <i className="ri-checkbox-circle-line text-emerald-600 text-sm" />
                    </div>
                    <p className="font-body text-xs text-emerald-700">
                      {bankSaveSuccess}
                    </p>
                  </div>
                )}

                {isFormReadOnly ? (
                  <button
                    disabled
                    className="w-full font-body text-base font-semibold px-6 py-3.5 rounded-full bg-stone-200 text-stone-500 cursor-not-allowed whitespace-nowrap"
                  >
                    {verificationStatus === "pending"
                      ? "資料審核中，預計 1 個工作日內完成，請耐心等待"
                      : "實名認證已通過"}
                  </button>
                ) : (
                  <button
                    onClick={handleSaveBankInfo}
                    disabled={savingBankInfo}
                    className="w-full font-body text-base font-semibold px-6 py-3.5 rounded-full bg-stone-800 text-white hover:bg-stone-900 transition-colors whitespace-nowrap disabled:opacity-50 disabled:cursor-not-allowed"
                  >
                    {savingBankInfo ? (
                      <span className="flex items-center justify-center gap-2">
                        <i className="ri-loader-4-line animate-spin" />
                        儲存中...
                      </span>
                    ) : (
                      "提交審核並儲存"
                    )}
                  </button>
                )}

                {savingBankInfo && (
                  <p className="font-body text-[11px] text-stone-400 text-center leading-relaxed">
                    正在上傳證件照片，請保持網路暢通不要關閉頁面。
                    若超過一分鐘仍未完成，請按一次重新整理再試。
                  </p>
                )}
              </div>
            </div>

            {/* ===== Withdrawal Application ===== */}
            <div
              className={`bg-white rounded-2xl border overflow-hidden mt-6 transition-opacity ${
                isVerified ? "border-stone-200" : "border-stone-200 opacity-60"
              }`}
            >
              <div className="px-6 py-5 border-b border-stone-100">
                <h2 className="font-display text-base font-bold text-stone-800">
                  <i className="ri-arrow-left-down-line text-rose-700 mr-1.5" />
                  提款申請
                </h2>
              </div>
              <div className="p-6">
                <div className="flex flex-col gap-5">
                  {!isVerified && (
                    <div className="bg-stone-100 border border-stone-200 rounded-xl p-3 flex items-start gap-2">
                      <div className="w-4 h-4 flex items-center justify-center flex-shrink-0 mt-0.5">
                        <i className="ri-lock-line text-stone-500 text-sm" />
                      </div>
                      <p className="font-body text-xs text-stone-500 leading-relaxed">
                        {verificationStatus === "pending"
                          ? "實名認證審核中，請等待管理員審核通過後即可進行提款申請。"
                          : verificationStatus === "rejected"
                            ? "實名認證已被拒絕，請重新提交正確的認證資料或聯繫客服協助處理。"
                            : "請先完成上方「實名認證與銀行綁定」後，即可進行提款申請。"}
                      </p>
                    </div>
                  )}

                  <div>
                    <label className="font-body text-xs font-medium text-stone-500 mb-1.5 block">
                      提款金額（NT$）
                    </label>
                    <input
                      type="text"
                      inputMode="numeric"
                      value={amount}
                      onChange={handleAmountChange}
                      placeholder={isVerified ? "請輸入提款金額" : "完成認證後可使用"}
                      disabled={!isVerified}
                      className="w-full font-body text-lg px-4 py-3 rounded-xl border border-stone-200 focus:border-rose-400 focus:ring-2 focus:ring-rose-100 outline-none transition-all placeholder:text-stone-300 disabled:bg-stone-50 disabled:text-stone-400 disabled:cursor-not-allowed"
                    />

                  </div>

                  {/* Messages */}
                  {errorMsg && (
                    <div className="bg-red-50 border border-red-200 rounded-xl p-3 flex items-start gap-2">
                      <div className="w-4 h-4 flex items-center justify-center flex-shrink-0 mt-0.5">
                        <i className="ri-error-warning-line text-red-500 text-sm" />
                      </div>
                      <p className="font-body text-xs text-red-600">{errorMsg}</p>
                    </div>
                  )}
                  {successMsg && (
                    <div className="bg-emerald-50 border border-emerald-200 rounded-xl p-3 flex items-start gap-2">
                      <div className="w-4 h-4 flex items-center justify-center flex-shrink-0 mt-0.5">
                        <i className="ri-checkbox-circle-line text-emerald-600 text-sm" />
                      </div>
                      <p className="font-body text-xs text-emerald-700">
                        {successMsg}
                      </p>
                    </div>
                  )}

                  <button
                    onClick={handleSubmitClick}
                    disabled={submitting || !amount || !isVerified || isSuspended || isBanned}
                    className="w-full font-body text-base font-semibold px-6 py-3.5 rounded-full bg-rose-800 text-white hover:bg-rose-900 transition-colors whitespace-nowrap disabled:opacity-50 disabled:cursor-not-allowed"
                  >
                    {submitting ? (
                      <span className="flex items-center justify-center gap-2">
                        <i className="ri-loader-4-line animate-spin" />
                        處理中...
                      </span>
                    ) : (
                      "確認提款"
                    )}
                  </button>

                  {isBanned && (
                    <div className="bg-red-50 border border-red-200 rounded-xl p-3 flex items-start gap-2">
                      <div className="w-4 h-4 flex items-center justify-center flex-shrink-0 mt-0.5">
                        <i className="ri-error-warning-line text-red-500 text-sm" />
                      </div>
                      <p className="font-body text-xs text-red-600">
                        您的帳號已被封鎖，無法進行提款操作，請聯繫客服人員。
                      </p>
                    </div>
                  )}

                  {isSuspended && (
                    <div className="bg-amber-50 border border-amber-200 rounded-xl p-3 flex items-start gap-2">
                      <div className="w-4 h-4 flex items-center justify-center flex-shrink-0 mt-0.5">
                        <i className="ri-error-warning-line text-amber-600 text-sm" />
                      </div>
                      <p className="font-body text-xs text-amber-700">
                        您的帳號目前處於暫停狀態，暫時無法申請提現，請聯繫客服。
                      </p>
                    </div>
                  )}
                </div>
              </div>
            </div>

            {/* Withdrawal History */}
            <div className="bg-white rounded-2xl border border-stone-200 overflow-hidden mt-6">
              <div className="px-6 py-5 border-b border-stone-100">
                <h2 className="font-display text-base font-bold text-stone-800">
                  <i className="ri-history-line text-rose-700 mr-1.5" />
                  提款記錄
                </h2>
              </div>
              <div className="p-0">
                <WithdrawalHistory userId={user?.id || ""} />
              </div>
            </div>
          </div>
        </div>
      </main>

      <Footer />
    </div>
  );
}

function WithdrawalHistory({ userId }: { userId: string }) {
  const [records, setRecords] = useState<
    Array<{
      id: string;
      amount: number;
      status: string;
      bank_name: string | null;
      created_at: string;
    }>
  >([]);
  const [historyLoading, setHistoryLoading] = useState(true);
  const [historyError, setHistoryError] = useState("");

  const fetchHistory = useCallback(async (signal?: AbortSignal) => {
    if (!userId) return;
    setHistoryLoading(true);
    setHistoryError("");
    try {
      const { data, error } = await supabase
        .from("withdrawals")
        .select("id, amount, status, bank_name, created_at")
        .eq("user_id", userId)
        .eq("type", "withdrawal")
        .order("created_at", { ascending: false })
        .limit(20)
        .abortSignal(signal);

      if (signal?.aborted) return;

      if (error) {
        console.error("[fetchHistory] error:", error);
        setHistoryError(`載入失敗：${error.message}`);
      } else {
        setRecords(data || []);
      }
    } catch (err: unknown) {
      if ((err as Error).name === "AbortError") return;
      console.error("[fetchHistory] exception:", err);
      const msg = err instanceof Error ? err.message : "載入過程發生錯誤";
      setHistoryError(msg);
    } finally {
      if (!signal?.aborted) {
        setHistoryLoading(false);
      }
    }
  }, [userId]);

  // Fetch on mount — no fetchedRef guard, always fresh
  useEffect(() => {
    if (!userId) return;
    // 清除舊的 sessionStorage 提領紀錄快取，強制即時刷新
    try {
      sessionStorage.removeItem("withdrawal_records_cache");
      sessionStorage.removeItem("withdrawal_history");
    } catch {
      /* ignore */
    }
    const controller = new AbortController();
    fetchHistory(controller.signal);
    return () => controller.abort();
  }, [userId, fetchHistory]);

  // Refetch when tab becomes visible (user returns from background / admin updated)
  useEffect(() => {
    const handleVisibilityChange = () => {
      if (document.visibilityState === "visible") {
        fetchHistory();
      }
    };
    document.addEventListener("visibilitychange", handleVisibilityChange);
    return () => document.removeEventListener("visibilitychange", handleVisibilityChange);
  }, [fetchHistory]);

  if (historyLoading && records.length === 0) {
    return (
      <div className="px-6 py-8 text-center">
        <i className="ri-loader-4-line text-2xl text-stone-300 animate-spin" />
        <p className="font-body text-xs text-stone-400 mt-2">載入中...</p>
      </div>
    );
  }

  if (historyError && records.length === 0) {
    return (
      <div className="px-6 py-8 text-center">
        <div className="w-10 h-10 rounded-full bg-red-50 flex items-center justify-center mx-auto mb-3">
          <i className="ri-error-warning-line text-red-500 text-lg" />
        </div>
        <p className="font-body text-sm text-stone-600 mb-2">{historyError}</p>
        <button
          onClick={() => fetchHistory()}
          className="font-body text-sm font-medium px-4 py-2 rounded-full border border-stone-300 text-stone-600 hover:bg-stone-50 transition-colors whitespace-nowrap cursor-pointer"
        >
          <i className="ri-refresh-line mr-1" />
          重新載入
        </button>
      </div>
    );
  }

  if (records.length === 0) {
    return (
      <div className="px-6 py-8 text-center">
        <p className="font-body text-sm text-stone-400">尚無提款記錄</p>
      </div>
    );
  }

  return (
    <div className="overflow-x-auto">
      <table className="w-full">
        <thead>
          <tr className="border-b border-stone-100 bg-stone-50/50">
            {["申請日期", "金額", "銀行", "狀態"].map((h) => (
              <th
                key={h}
                className="font-body text-[11px] font-semibold text-stone-600 text-left px-4 py-3 whitespace-nowrap"
              >
                {h}
              </th>
            ))}
          </tr>
        </thead>
        <tbody className="divide-y divide-stone-100">
          {records.map((r) => {
            const s =
              WITHDRAWAL_STATUS_MAP[r.status] || {
                label: r.status || "未知",
                bg: "bg-stone-50",
                text: "text-stone-500",
                border: "border-stone-200",
              };
            return (
              <tr key={r.id}>
                <td className="px-4 py-3 font-body text-xs text-stone-600">
                  {r.created_at?.slice(0, 10) || "—"}
                </td>
                <td className="px-4 py-3 font-body text-sm font-bold text-stone-800">
                  NT$ {r.amount.toLocaleString()}
                </td>
                <td className="px-4 py-3 font-body text-xs text-stone-500">
                  {r.bank_name || "—"}
                </td>
                <td className="px-4 py-3">
                  <span
                    className={`inline-flex items-center font-body text-[10px] font-semibold px-2 py-1 rounded-full border ${s.bg} ${s.text} ${s.border}`}
                  >
                    {s.label}
                  </span>
                </td>
              </tr>
            );
          })}
        </tbody>
      </table>
    </div>
  );
}